import React, { useEffect, useState, Component } from 'react';
import { Wall, Opening, Obstacle, Soffit, Point } from '../types/room';
import { distance, formatDistance } from '../utils/geometry';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, RotateCw, ZoomIn, ZoomOut, Maximize2, Play, Pause, Eye } from 'lucide-react';
interface Canvas3DProps {
  walls: Wall[];
  openings: Opening[];
  obstacles: Obstacle[];
  soffits: Soffit[];
  roomHeight: number;
  roomRotation: number;
}
export function Canvas3D({
  walls,
  openings,
  obstacles,
  soffits,
  roomHeight,
  roomRotation
}: Canvas3DProps) {
  const [viewRotationY, setViewRotationY] = useState(0); // Y-axis rotation
  const [viewRotationX, setViewRotationX] = useState(30); // X-axis tilt
  const [zoomLevel, setZoomLevel] = useState(2.5);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0
  });
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  // Auto-rotation effect
  useEffect(() => {
    if (!isAutoRotating) return;
    const interval = setInterval(() => {
      setViewRotationY(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, [isAutoRotating]);
  if (walls.length === 0) {
    return <div className="w-full h-full flex items-center justify-center bg-white" data-id="element-261">
        <div className="text-center" data-id="element-262">
          <div className="text-black text-6xl mb-4" data-id="element-263">🏗️</div>
          <div className="text-gray-800 text-lg font-medium" data-id="element-264">
            Draw walls to see 3D view
          </div>
          <div className="text-gray-600 text-sm mt-2" data-id="element-265">
            Switch to TOP view and start building
          </div>
        </div>
      </div>;
  }
  // Enhanced isometric projection with Y-axis rotation and zoom
  const project = (x: number, y: number, z: number): Point => {
    const scale = zoomLevel;
    // Apply Y-axis rotation
    const radY = viewRotationY * Math.PI / 180;
    const rotatedX = x * Math.cos(radY) - y * Math.sin(radY);
    const rotatedY = x * Math.sin(radY) + y * Math.cos(radY);
    // Apply X-axis tilt
    const radX = viewRotationX * Math.PI / 180;
    const tiltedZ = z * Math.cos(radX);
    const tiltedY = rotatedY - z * Math.sin(radX);
    // Isometric projection
    const angleX = Math.PI / 6;
    const angleY = Math.PI / 6;
    const isoX = (rotatedX - tiltedY) * Math.cos(angleX) * scale;
    const isoY = (rotatedX + tiltedY) * Math.sin(angleY) * scale - tiltedZ * scale;
    return {
      x: isoX,
      y: isoY
    };
  };
  // Calculate room bounds
  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;
  walls.forEach(wall => {
    minX = Math.min(minX, wall.start.x, wall.end.x);
    maxX = Math.max(maxX, wall.start.x, wall.end.x);
    minY = Math.min(minY, wall.start.y, wall.end.y);
    maxY = Math.max(maxY, wall.start.y, wall.end.y);
  });
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const viewWidth = 2000;
  const viewHeight = 1500;
  const offsetX = viewWidth / 2;
  const offsetY = viewHeight / 2 + 200;
  const wallThickness = 35;
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    setIsAutoRotating(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      // Horizontal drag = Y-axis rotation
      setViewRotationY(prev => (prev + deltaX * 0.5) % 360);
      // Vertical drag = X-axis tilt (limited range)
      setViewRotationX(prev => Math.max(0, Math.min(60, prev - deltaY * 0.3)));
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoomLevel(prev => Math.max(1, Math.min(5, prev * delta)));
  };
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(5, prev * 1.2));
  };
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(1, prev * 0.8));
  };
  const handleReset = () => {
    setViewRotationY(0);
    setViewRotationX(30);
    setZoomLevel(2.5);
    setIsAutoRotating(false);
  };
  const handlePresetView = (preset: 'front' | 'back' | 'left' | 'right' | 'top') => {
    setIsAutoRotating(false);
    switch (preset) {
      case 'front':
        setViewRotationY(0);
        setViewRotationX(30);
        break;
      case 'back':
        setViewRotationY(180);
        setViewRotationX(30);
        break;
      case 'left':
        setViewRotationY(270);
        setViewRotationX(30);
        break;
      case 'right':
        setViewRotationY(90);
        setViewRotationX(30);
        break;
      case 'top':
        setViewRotationY(0);
        setViewRotationX(0);
        break;
    }
  };
  const roomCorners = walls.map(wall => wall.start);
  return <div className="w-full h-full bg-white overflow-hidden relative" style={{
    cursor: isDragging ? 'grabbing' : 'grab'
  }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onWheel={handleWheel} data-id="element-266">
      <svg width="100%" height="100%" viewBox={`0 0 ${viewWidth} ${viewHeight}`} className="w-full h-full" data-id="element-267">
        <defs data-id="element-268">
          <linearGradient id="wall-gradient-pro" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-269">
            <stop offset="0%" stopColor="#e5e5e5" data-id="element-270" />
            <stop offset="100%" stopColor="#d4d4d4" data-id="element-271" />
          </linearGradient>
          <linearGradient id="floor-gradient-pro" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-272">
            <stop offset="0%" stopColor="#f5f5f5" data-id="element-273" />
            <stop offset="100%" stopColor="#e5e5e5" data-id="element-274" />
          </linearGradient>

          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-275">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3" data-id="element-276" />
            <feOffset dx="2" dy="2" result="offsetblur" data-id="element-277" />
            <feComponentTransfer data-id="element-278">
              <feFuncA type="linear" slope="0.3" data-id="element-279" />
            </feComponentTransfer>
            <feMerge data-id="element-280">
              <feMergeNode data-id="element-281" />
              <feMergeNode in="SourceGraphic" data-id="element-282" />
            </feMerge>
          </filter>

          <marker id="dim-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" data-id="element-283">
            <path d="M 0 0 L 8 4 L 0 8 Z" fill="#000" data-id="element-284" />
          </marker>
        </defs>

        <g data-id="element-285">
          {/* Floor */}
          <motion.g initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-286">
            <polygon points={walls.map(wall => {
            const p = project(wall.start.x - centerX, wall.start.y - centerY, 0);
            return `${p.x + offsetX},${p.y + offsetY}`;
          }).join(' ')} fill="url(#floor-gradient-pro)" stroke="#000" strokeWidth={2} filter="url(#shadow)" data-id="element-287" />

            {/* Floor grid */}
            {Array.from({
            length: 10
          }).map((_, i) => {
            const step = (maxX - minX) / 10;
            const x = minX + i * step;
            const start = project(x - centerX, minY - centerY, 0);
            const end = project(x - centerX, maxY - centerY, 0);
            return <line key={`grid-x-${i}`} x1={start.x + offsetX} y1={start.y + offsetY} x2={end.x + offsetX} y2={end.y + offsetY} stroke="#d4d4d4" strokeWidth={0.5} opacity={0.5} data-id="element-288" />;
          })}
          </motion.g>

          {/* Walls */}
          {walls.map((wall, index) => {
          const wallLength = distance(wall.start, wall.end);
          const bottomStart = project(wall.start.x - centerX, wall.start.y - centerY, 0);
          const bottomEnd = project(wall.end.x - centerX, wall.end.y - centerY, 0);
          const topStart = project(wall.start.x - centerX, wall.start.y - centerY, roomHeight);
          const topEnd = project(wall.end.x - centerX, wall.end.y - centerY, roomHeight);
          const wallOpenings = openings.filter(o => o.wallId === wall.id);
          return <motion.g key={wall.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05,
            duration: 0.3
          }} data-id="element-289">
                <polygon points={`
                    ${bottomStart.x + offsetX},${bottomStart.y + offsetY}
                    ${bottomEnd.x + offsetX},${bottomEnd.y + offsetY}
                    ${topEnd.x + offsetX},${topEnd.y + offsetY}
                    ${topStart.x + offsetX},${topStart.y + offsetY}
                  `} fill="url(#wall-gradient-pro)" stroke="#000" strokeWidth={2} filter="url(#shadow)" data-id="element-290" />

                <text x={(bottomStart.x + bottomEnd.x) / 2 + offsetX} y={(bottomStart.y + bottomEnd.y) / 2 + offsetY + 20} fill="#000" fontSize={12} fontWeight="600" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-291">
                  WALL {index + 1}
                </text>

                <g data-id="element-292">
                  <line x1={bottomStart.x + offsetX} y1={bottomStart.y + offsetY - 30} x2={bottomEnd.x + offsetX} y2={bottomEnd.y + offsetY - 30} stroke="#000" strokeWidth={1.5} markerStart="url(#dim-arrow)" markerEnd="url(#dim-arrow)" data-id="element-293" />
                  <rect x={(bottomStart.x + bottomEnd.x) / 2 + offsetX - 35} y={(bottomStart.y + bottomEnd.y) / 2 + offsetY - 45} width={70} height={20} fill="white" stroke="#000" strokeWidth={1} rx={2} data-id="element-294" />
                  <text x={(bottomStart.x + bottomEnd.x) / 2 + offsetX} y={(bottomStart.y + bottomEnd.y) / 2 + offsetY - 30} fill="#000" fontSize={13} fontWeight="700" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-295">
                    {formatDistance(wallLength)}
                  </text>
                </g>

                {wallOpenings.map((opening, oIndex) => {
              const ratio = opening.position / wallLength;
              const openingX = wall.start.x + (wall.end.x - wall.start.x) * ratio;
              const openingY = wall.start.y + (wall.end.y - wall.start.y) * ratio;
              const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
              const perpX = Math.cos(wallAngle) * (opening.width / 2);
              const perpY = Math.sin(wallAngle) * (opening.width / 2);
              const leftBottom = project(openingX - perpX - centerX, openingY - perpY - centerY, opening.elevation);
              const rightBottom = project(openingX + perpX - centerX, openingY + perpY - centerY, opening.elevation);
              const leftTop = project(openingX - perpX - centerX, openingY - perpY - centerY, opening.elevation + opening.height);
              const rightTop = project(openingX + perpX - centerX, openingY + perpY - centerY, opening.elevation + opening.height);
              return <g key={opening.id} data-id="element-296">
                      <polygon points={`
                          ${leftBottom.x + offsetX},${leftBottom.y + offsetY}
                          ${rightBottom.x + offsetX},${rightBottom.y + offsetY}
                          ${rightTop.x + offsetX},${rightTop.y + offsetY}
                          ${leftTop.x + offsetX},${leftTop.y + offsetY}
                        `} fill={opening.type === 'window' ? '#f0f0f0' : '#e5e5e5'} stroke="#000" strokeWidth={2} data-id="element-297" />

                      <text x={(leftBottom.x + rightBottom.x) / 2 + offsetX} y={(leftBottom.y + rightBottom.y) / 2 + offsetY} fill="#000" fontSize={10} fontWeight="600" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-298">
                        {opening.type === 'window' ? 'WINDOW' : 'DOOR'}{' '}
                        {oIndex + 1}
                      </text>
                    </g>;
            })}
              </motion.g>;
        })}

          {/* Obstacles */}
          {obstacles.map((obstacle, index) => {
          const corners = [{
            x: -obstacle.width / 2,
            y: -obstacle.depth / 2
          }, {
            x: obstacle.width / 2,
            y: -obstacle.depth / 2
          }, {
            x: obstacle.width / 2,
            y: obstacle.depth / 2
          }, {
            x: -obstacle.width / 2,
            y: obstacle.depth / 2
          }];
          const bottomCorners = corners.map(c => project(obstacle.position.x + c.x - centerX, obstacle.position.y + c.y - centerY, 0));
          const topCorners = corners.map(c => project(obstacle.position.x + c.x - centerX, obstacle.position.y + c.y - centerY, obstacle.height));
          return <motion.g key={obstacle.id} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3 + index * 0.05,
            duration: 0.3
          }} data-id="element-299">
                <polygon points={topCorners.map(p => `${p.x + offsetX},${p.y + offsetY}`).join(' ')} fill="#d4d4d4" stroke="#000" strokeWidth={2} filter="url(#shadow)" data-id="element-300" />

                <polygon points={`
                    ${bottomCorners[0].x + offsetX},${bottomCorners[0].y + offsetY}
                    ${bottomCorners[1].x + offsetX},${bottomCorners[1].y + offsetY}
                    ${topCorners[1].x + offsetX},${topCorners[1].y + offsetY}
                    ${topCorners[0].x + offsetX},${topCorners[0].y + offsetY}
                  `} fill="#e5e5e5" stroke="#000" strokeWidth={2} data-id="element-301" />

                <polygon points={`
                    ${bottomCorners[1].x + offsetX},${bottomCorners[1].y + offsetY}
                    ${bottomCorners[2].x + offsetX},${bottomCorners[2].y + offsetY}
                    ${topCorners[2].x + offsetX},${topCorners[2].y + offsetY}
                    ${topCorners[1].x + offsetX},${topCorners[1].y + offsetY}
                  `} fill="#c4c4c4" stroke="#000" strokeWidth={2} data-id="element-302" />

                <text x={(topCorners[0].x + topCorners[2].x) / 2 + offsetX} y={(topCorners[0].y + topCorners[2].y) / 2 + offsetY} fill="#000" fontSize={12} fontWeight="700" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-303">
                  OBSTACLE {index + 1}
                </text>
              </motion.g>;
        })}

          {/* Soffits */}
          {soffits.map((soffit, index) => {
          const bottomPoints = soffit.points.map(p => project(p.x - centerX, p.y - centerY, roomHeight - soffit.drop));
          const topPoints = soffit.points.map(p => project(p.x - centerX, p.y - centerY, roomHeight));
          const centerPoint = {
            x: soffit.points.reduce((sum, p) => sum + p.x, 0) / soffit.points.length,
            y: soffit.points.reduce((sum, p) => sum + p.y, 0) / soffit.points.length
          };
          const centerProj = project(centerPoint.x - centerX, centerPoint.y - centerY, roomHeight - soffit.drop / 2);
          return <motion.g key={soffit.id} initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + index * 0.05,
            duration: 0.3
          }} data-id="element-304">
                <polygon points={bottomPoints.map(p => `${p.x + offsetX},${p.y + offsetY}`).join(' ')} fill="#e5e5e5" stroke="#000" strokeWidth={2} strokeDasharray="4,2" data-id="element-305" />

                {soffit.points.map((point, i) => {
              const nextIndex = (i + 1) % soffit.points.length;
              return <polygon key={`soffit-side-${i}`} points={`
                        ${bottomPoints[i].x + offsetX},${bottomPoints[i].y + offsetY}
                        ${bottomPoints[nextIndex].x + offsetX},${bottomPoints[nextIndex].y + offsetY}
                        ${topPoints[nextIndex].x + offsetX},${topPoints[nextIndex].y + offsetY}
                        ${topPoints[i].x + offsetX},${topPoints[i].y + offsetY}
                      `} fill="#d4d4d4" stroke="#000" strokeWidth={1.5} data-id="element-306" />;
            })}

                <text x={centerProj.x + offsetX} y={centerProj.y + offsetY} fill="#000" fontSize={12} fontWeight="700" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-307">
                  CEILING SOFFIT {index + 1}
                </text>
              </motion.g>;
        })}
        </g>

        {/* Title */}
        <text x={viewWidth / 2} y={40} fill="#000" fontSize={20} fontWeight="700" fontFamily="Arial, sans-serif" textAnchor="middle" letterSpacing={2} data-id="element-308">
          3D ARCHITECTURAL VIEW
        </text>

        {/* Scale */}
        <g transform={`translate(40, ${viewHeight - 60})`} data-id="element-309">
          <line x1={0} y1={0} x2={100} y2={0} stroke="#000" strokeWidth={2} data-id="element-310" />
          <line x1={0} y1={-5} x2={0} y2={5} stroke="#000" strokeWidth={2} data-id="element-311" />
          <line x1={100} y1={-5} x2={100} y2={5} stroke="#000" strokeWidth={2} data-id="element-312" />
          <text x={50} y={20} fill="#000" fontSize={12} fontWeight="600" fontFamily="Arial, sans-serif" textAnchor="middle" data-id="element-313">
            SCALE: 1:50
          </text>
        </g>
      </svg>

      {/* Enhanced Control Panel - Bottom */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col gap-3 pointer-events-auto" data-id="element-314">
        {/* Main Controls */}
        <div className="flex items-center gap-3 bg-white border-2 border-black rounded-lg px-4 py-3 shadow-2xl" data-id="element-315">
          {/* Rotation Controls */}
          <div className="flex items-center gap-2" data-id="element-316">
            <motion.button whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={e => {
            e.stopPropagation();
            setViewRotationY(prev => (prev - 45) % 360);
            setIsAutoRotating(false);
          }} className="w-10 h-10 flex items-center justify-center bg-black hover:bg-gray-800 rounded-lg text-white transition-colors" title="Rotate Left (45°)" data-id="element-317">
              <RotateCcw size={20} data-id="element-318" />
            </motion.button>

            <div className="flex flex-col items-center gap-1 min-w-[80px]" data-id="element-319">
              <div className="text-gray-600 text-xs font-semibold tracking-wider" data-id="element-320">
                Y-AXIS
              </div>
              <div className="text-black text-lg font-bold font-mono" data-id="element-321">
                {Math.round(viewRotationY % 360)}°
              </div>
            </div>

            <motion.button whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={e => {
            e.stopPropagation();
            setViewRotationY(prev => (prev + 45) % 360);
            setIsAutoRotating(false);
          }} className="w-10 h-10 flex items-center justify-center bg-black hover:bg-gray-800 rounded-lg text-white transition-colors" title="Rotate Right (45°)" data-id="element-322">
              <RotateCw size={20} data-id="element-323" />
            </motion.button>
          </div>

          <div className="w-px h-12 bg-gray-300" data-id="element-324" />

          {/* Zoom Controls */}
          <div className="flex items-center gap-2" data-id="element-325">
            <motion.button whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={e => {
            e.stopPropagation();
            handleZoomOut();
          }} className="w-10 h-10 flex items-center justify-center bg-black hover:bg-gray-800 rounded-lg text-white transition-colors" title="Zoom Out" data-id="element-326">
              <ZoomOut size={20} data-id="element-327" />
            </motion.button>

            <div className="flex flex-col items-center gap-1 min-w-[80px]" data-id="element-328">
              <div className="text-gray-600 text-xs font-semibold tracking-wider" data-id="element-329">
                ZOOM
              </div>
              <div className="text-black text-lg font-bold font-mono" data-id="element-330">
                {Math.round(zoomLevel * 40)}%
              </div>
            </div>

            <motion.button whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={e => {
            e.stopPropagation();
            handleZoomIn();
          }} className="w-10 h-10 flex items-center justify-center bg-black hover:bg-gray-800 rounded-lg text-white transition-colors" title="Zoom In" data-id="element-331">
              <ZoomIn size={20} data-id="element-332" />
            </motion.button>
          </div>

          <div className="w-px h-12 bg-gray-300" data-id="element-333" />

          {/* Auto-Rotate */}
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={e => {
          e.stopPropagation();
          setIsAutoRotating(!isAutoRotating);
        }} className={`px-4 h-10 flex items-center gap-2 rounded-lg text-sm font-semibold transition-colors ${isAutoRotating ? 'bg-black text-white' : 'bg-white border-2 border-black text-black hover:bg-gray-100'}`} title="Auto Rotate" data-id="element-334">
            {isAutoRotating ? <Pause size={16} data-id="element-335" /> : <Play size={16} data-id="element-336" />}
            {isAutoRotating ? 'PAUSE' : 'AUTO'}
          </motion.button>

          {/* Reset */}
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={e => {
          e.stopPropagation();
          handleReset();
        }} className="px-4 h-10 flex items-center justify-center bg-white hover:bg-gray-100 border-2 border-black rounded-lg text-black text-sm font-semibold transition-colors" title="Reset View" data-id="element-337">
            <Maximize2 size={16} className="mr-2" data-id="element-338" />
            RESET
          </motion.button>
        </div>

        {/* Preset Views */}
        <div className="flex items-center gap-2 bg-white border-2 border-black rounded-lg px-3 py-2 shadow-2xl" data-id="element-339">
          <Eye size={16} className="text-gray-600" data-id="element-340" />
          <span className="text-xs font-semibold text-gray-600 tracking-wider" data-id="element-341">
            VIEWS:
          </span>
          {(['front', 'right', 'back', 'left', 'top'] as const).map(view => <motion.button key={view} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={e => {
          e.stopPropagation();
          handlePresetView(view);
        }} className="px-3 py-1 bg-white hover:bg-black hover:text-white border border-black rounded text-xs font-semibold uppercase transition-colors" data-id="element-342">
              {view}
            </motion.button>)}
        </div>
      </div>

      {/* Instructions */}
      <AnimatePresence data-id="element-343">
        {!isDragging && !isAutoRotating && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-white border-2 border-black rounded-lg px-4 py-2 pointer-events-none shadow-lg" data-id="element-344">
            <div className="text-black text-sm font-semibold" data-id="element-345">
              🖱️ DRAG TO ROTATE • SCROLL TO ZOOM • USE BUTTONS FOR PRECISE
              CONTROL
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Auto-Rotating Indicator */}
      <AnimatePresence data-id="element-346">
        {isAutoRotating && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg pointer-events-none flex items-center gap-2" data-id="element-347">
            <Play size={16} data-id="element-348" />
            AUTO-ROTATING...
          </motion.div>}
      </AnimatePresence>

      {/* Zoom Level Indicator */}
      <div className="absolute top-8 right-8 bg-white border-2 border-black rounded-lg px-3 py-2 shadow-lg pointer-events-none" data-id="element-349">
        <div className="text-xs font-semibold text-gray-600 tracking-wider mb-1" data-id="element-350">
          ZOOM LEVEL
        </div>
        <div className="text-2xl font-bold text-black font-mono" data-id="element-351">
          {Math.round(zoomLevel * 40)}%
        </div>
      </div>

      {/* Rotation Indicator */}
      <div className="absolute top-8 left-8 bg-white border-2 border-black rounded-lg px-3 py-2 shadow-lg pointer-events-none" data-id="element-352">
        <div className="text-xs font-semibold text-gray-600 tracking-wider mb-1" data-id="element-353">
          VIEW ANGLE
        </div>
        <div className="text-2xl font-bold text-black font-mono" data-id="element-354">
          {Math.round(viewRotationY % 360)}°
        </div>
        <div className="text-xs text-gray-600 mt-1" data-id="element-355">
          Tilt: {Math.round(viewRotationX)}°
        </div>
      </div>
    </div>;
}